/**
 * The seven standing between the sima and the finish, and the tornimajoitus
 * behind them with its own door.
 *
 * The porukka is the last thing in the way of the chair, and the one box in
 * the hall that moves: it is hashed, and pouring the sima is what shifts it.
 */

import {
  decor,
  handle,
  type DecorSpec,
  type FloorSpec,
  type HandleSpec,
  type MaterialKey,
  type PorukkaSpec,
  type ShirtPrint,
  type SimaSpec,
  type SitterCap,
} from '../world/Level';
import { yawTowards } from '../utils/math';
import { BAARI, SIMATUOPPI } from './levelBaari';
import {
  PUPIS_TEAM_NAME,
  TABLE_CENTERS,
  TABLE_HALF_WIDTH,
  TABLE_Z,
  TUOPPI,
} from './levelJuhlasali';
import { ORGA_TARGET } from './levelSaliVaki';

/* ------------------------------------ porukka keskellä salin lattiaa ----- */

/**
 * ==========================================================================
 *  THE PORUKKA IN THE MIDDLE OF THE HALL
 * ==========================================================================
 *  The first thing in the juhlasali is the baari, and the second is everybody
 *  who has already been to it. Seven of them are standing on the open floor a
 *  stride past the glasses, in two loose ranks that reach from the tables to
 *  the south wall - which is the whole width of the runway - and one more is
 *  out in front of them, who is the one who opens his mouth:
 *
 *      "Et kai sä 'Kimmo' selvinpäin tullut pelaamaan? Ota vaikka yks sima
 *       eka."
 *
 *  See `src/world/Sima.ts` for what it takes to move them and
 *  `src/config/sima.ts` for what that costs.
 *
 *  ------------------------------------------------------------------------
 *   WHY THE MIDDLE OF THE ROOM AND NOT THE FAR CORNER
 *  ------------------------------------------------------------------------
 *  They used to stand at the other end of the hall, filling the 2.5 m
 *  corridor between pöytä 5 and the east wall, and the toll was the last
 *  thing before the chair. That left the runway's two lanes open for the
 *  whole fifteen metres, which was the argument for it. The argument against
 *  is that it put the demand and the two things it asks for at opposite ends
 *  of the room: a crowd sixteen metres away shouted for a drink that was
 *  standing at the runner's elbow while they shouted, and the wall it was
 *  worth was a wall he met a hall later.
 *
 *  The wall is where the drink is now. It stands immediately east of the
 *  tuoppipöytä, so the sentence comes from people the runner is looking
 *  straight at, with the stack of empties and the two hanat a couple of
 *  strides behind the man saying it, and the toll is paid where it is asked
 *  instead of carried the length of a room.
 *
 *  What that costs is the honest half. A wall here closes both lanes at once,
 *  which the corner version deliberately did not (README, *Kaksi linjaa joka
 *  pätkällä*). The trade is which end of the hall it closes them at: the
 *  choice of side used to be offered for fifteen metres and then charged for,
 *  and it is now charged for in the first seven and offered for the nine that
 *  are left - which are nine metres run with a full glass in the hand and
 *  nothing else to buy.
 *
 *  It is also simply where a crowd would be. Nobody at nine in the morning is
 *  standing in a line along a wall; they are standing on the floor between
 *  the bar and the tables with a glass each, which is where everybody who has
 *  just been served ends up - and it is what entitles the ones in the way to
 *  ask.
 *
 *  ------------------------------------------------------------------------
 *   AND WHERE THEY GO
 *  ------------------------------------------------------------------------
 *  Back up the aisle behind them, between pöytä 2 and pöytä 3, in two short
 *  files with the one who spoke at the mouth of it.
 *
 *  There is nowhere else, and that is not a figure of speech. Both lanes have
 *  to end up clear or the finalist runs through somebody: the wall-side lane
 *  is the one the pour is made in, and the table-side lane is 1.1 m of floor
 *  whose entire point is that there is nothing on it. The aisles are the one
 *  part of this room that was never a route - five-metre dead ends with
 *  somebody's team in them - and this is the only one of the four with nobody
 *  asleep in it; `nukkuja-6` remains in the first aisle as the shortcut step.
 * ==========================================================================
 */
export const PORUKKA = {
  /**
   * The box that is them while they are in the way: across the runway from
   * the front edge of the tables to the inside face of the south wall, so
   * that neither lane is a way round.
   *
   * How deep it is was decided by the floor rather than chosen. `nukkuja-2`
   * ends at `x = 137.75` and `nukkuja-3` begins at `138.85`, which leaves
   * 1.1 m between two men asleep on the boards; this takes a metre of it and
   * leaves five centimetres at either end. That 5 cm is not a seam anybody
   * can use - the box spans the runway on its own and the sleepers are what
   * happens to be lying past its ends - it is there so that two colliders
   * cannot round into each other, which is a thing floating point does at the
   * fourth decimal and `Level.test.ts` notices.
   */
  minX: 137.8,
  maxX: 138.8,
  minZ: TABLE_Z[1],
  maxZ: BAARI.wallZ,
  /**
   * Taller than any jump the level can produce - the same 1.86 m argument
   * Mäysä's box is built on, rounded up because this one is a crowd rather
   * than one man. A toll you can hop is not a toll.
   */
  height: 1.9,
  /**
   * The two files they form once it is not a wall: the aisle between pöytä 2
   * and pöytä 3 is 1.6 m of floor, which is two men and the gap between them.
   */
  asideX: [137.05, 137.75] as [number, number],
} as const;

/**
 * What the one who speaks is pointing at: the near hana on the baari, at the
 * top of its own tower.
 *
 * Derived from the counter rather than typed out, so moving the bar moves the
 * arm. And aimed at the *tower* rather than at the counter it stands on,
 * because the sentence is about what comes out of it: an arm held level at
 * the handles is an arm pointing at the beer, and one dropped at the bar top
 * is an arm pointing at a piece of furniture four metres away.
 */
const PORUKKA_TARGET: [number, number, number] = [
  BAARI.tapX[0],
  BAARI.top + BAARI.towerHeight,
  BAARI.tapZ,
];

/**
 * The lippis three of them have on, and it is the same lippis on all three on
 * purpose.
 *
 * A hat printed for a team is the one thing at a LAN that says which people
 * arrived together, and it only says it if more than one man is wearing it -
 * one printed cap is a man in a hat, three of them across two ranks is a
 * porukka standing in somebody's way together. The one who does the talking
 * is wearing one, because the demand should come from somebody the picture
 * has already introduced.
 *
 * The name is `TEAM_NAMES`' own rather than typed out again, so the sign over
 * pöytä 2 and the caps on the floor in front of it cannot drift apart: these
 * are the men off that table, which is also the table the empty tuoppi used
 * to stand on.
 *
 * Ten letters, which is exactly what the kokki's `Ruokatiimi` is and is as
 * wide as a cap goes: `createSign` sizes its font off the *height* and does
 * not shrink to fit, and the panel is only as wide as the head it is fitted
 * to. A longer name would run off the front of the cap.
 */
const TAAS_PUPIS: SitterCap = {
  text: PUPIS_TEAM_NAME,
  color: '#141a24',
  background: '#c7532f',
};

/**
 * The seven of them: where each stands while the room is shut, where in the
 * aisle he goes once it is open, and what he is wearing.
 *
 * Six inside the box in two staggered ranks and the seventh a stride out in
 * front of it, which is the one who talks. A line that speaks out of the
 * middle of itself is a crowd making a noise; one man stepped forward out of
 * it with his arm out is somebody addressing you, and this toll is a question
 * rather than a wall. He has no more collider than the rest of them: the box
 * is what stops anybody, and he is standing in front of it.
 *
 * Staggered rather than shoulder to shoulder, because the box is 4.5 m wide
 * and six men pressed into a line across it would be a fence. Three quarters
 * of a metre apart in two ranks is a group of people standing about with
 * drinks, which is what this is, and it still reaches both walls.
 *
 * The angles are all somewhere near due west, because west is the doorway and
 * the bar and the runner coming off it. Two of them are turned well off that
 * for the same reason any group of people has somebody in it talking to the
 * man beside him rather than watching the door.
 */
const PORUKKA_JONO: readonly {
  stand: [number, number];
  rotationY: number;
  height: number;
  /** Where in the aisle he ends up once the tuoppi is full. */
  aside: [number, number];
  /** And which way he is turned there. See `PORUKKA.asideX`. */
  asideRotationY: number;
  cap?: SitterCap;
  shirt?: ShirtPrint;
}[] = [
  // Front rank: the three the runner is looking at, from the tables' edge
  // down to the wall lane.
  {
    stand: [138.1, -34.1],
    rotationY: -1.3,
    height: 1.78,
    aside: [PORUKKA.asideX[0], -34.95],
    asideRotationY: 0.35,
  },
  {
    stand: [138.1, -32.6],
    rotationY: -1.55,
    height: 1.86,
    aside: [PORUKKA.asideX[1], -35.6],
    asideRotationY: 0.5,
    cap: TAAS_PUPIS,
    shirt: { text: 'snottis', color: '#f4f1e8', background: '#7a2b3a' },
  },
  {
    stand: [138.1, -31.1],
    rotationY: -1.6,
    height: 1.73,
    aside: [PORUKKA.asideX[0], -35.6],
    asideRotationY: 0.3,
  },
  // Back rank, in the gaps of the front one.
  {
    stand: [138.5, -33.35],
    rotationY: -2.3,
    height: 1.9,
    aside: [PORUKKA.asideX[0], -36.25],
    asideRotationY: 0.55,
    cap: TAAS_PUPIS,
  },
  {
    stand: [138.5, -31.85],
    rotationY: -0.55,
    height: 1.76,
    aside: [PORUKKA.asideX[1], -36.25],
    asideRotationY: 0.25,
    shirt: { text: 'Zaza', color: '#f4f1e8', background: '#2b3f7a' },
  },
  {
    stand: [138.5, -30.5],
    rotationY: -1.45,
    height: 1.82,
    aside: [PORUKKA.asideX[0], -36.9],
    asideRotationY: 0.45,
  },
  // And the one out in front, who asks. He stands in the wall-side lane
  // because that is the lane the errand is on: a runner who has come through
  // the doorway and turned towards the bar has him between himself and the
  // rest of the room. His place afterwards is the mouth of the aisle, turned
  // east, so the arm that was pointing at the hanat ends up pointing at the
  // chair - the same move Mäysä makes when he steps out of the porstua and
  // turns his hand back into it.
  {
    stand: [137.45, -31.3],
    rotationY: -1.4,
    height: 1.84,
    aside: [PORUKKA.asideX[1], -34.95],
    asideRotationY: 1.55,
    cap: TAAS_PUPIS,
    shirt: { text: 'Damu', color: '#f4f1e8', background: '#4a2b6b' },
  },
];

/** The one who talks is the last of them, and the only one who raises an arm. */
const PORUKKA_PUHUJA = PORUKKA_JONO.length - 1;

const PORUKKA_CROWD: PorukkaSpec[] = PORUKKA_JONO.map((member, index) => ({
  name: `porukka-${index + 1}`,
  stand: member.stand,
  height: member.height,
  rotationY: member.rotationY,
  points: index === PORUKKA_PUHUJA ? PORUKKA_TARGET : undefined,
  cap: member.cap,
  shirt: member.shirt,
  aside: {
    stand: member.aside,
    rotationY: member.asideRotationY,
    // Kimmo's own monitor, taken from the box that is it - the same point the
    // orga has been pointing at from the end wall the whole run. One arm at
    // each end of the hall aimed at the same chair is the picture the aisle
    // is supposed to leave as the finalist goes past it.
    points: index === PORUKKA_PUHUJA ? ORGA_TARGET : undefined,
  },
}));

// Continue the runway crowd along the table ends. The first sleeper remains
// accessible west of the toll, but every exit behind it stays shut until the
// last table. 1.9 m also clears the maximum jump apex from a tabletop.
export const POYTAPORUKKA = {
  name: 'porukka-poytien-edessa',
  minX: PORUKKA.minX,
  maxX: TABLE_CENTERS[4] + TABLE_HALF_WIDTH,
  minZ: TABLE_Z[1],
  maxZ: TABLE_Z[1] + 0.6,
  height: 1.9,
} as const;

const POYTAPORUKKA_CROWD: PorukkaSpec[] = Array.from({ length: 18 }, (_, index) => {
  const spacing = (POYTAPORUKKA.maxX - POYTAPORUKKA.minX) / 18;
  // Three ranks in each of the two wide aisles, clear of the table ends.
  const aisle = index < 9 ? 2 : 3;
  const column = index % 3;
  const row = Math.floor((index % 9) / 3);
  const left = TABLE_CENTERS[aisle] + TABLE_HALF_WIDTH;
  const right = TABLE_CENTERS[aisle + 1] - TABLE_HALF_WIDTH;
  return {
    name: `poytaporukka-${index + 1}`,
    stand: [POYTAPORUKKA.minX + spacing * (index + 0.5), TABLE_Z[1] + 0.3],
    height: 1.76 + (index % 4) * 0.04,
    rotationY: Math.PI + (index % 3 - 1) * 0.25,
    aside: {
      stand: [left + (right - left) * (column + 1) / 4, -38.9 + row * 0.7],
      rotationY: (column - 1) * 0.35,
    },
  };
});

/**
 * The toll itself: the glass, the two hanat, and the box that is the crowd.
 *
 * The glass is the top of the near stack on the tuoppipöytä, taken from the
 * same helper that draws it, so the thing you can see on the table and the
 * thing F reaches for cannot end up in different places. Its point is the
 * middle of the glass rather than its foot, because that is where a hand
 * closes round one. The hanat are the nozzles rather than the towers - a pour
 * comes out of the spout, and the spout is what a hand goes to.
 */
export const SIMA_SPEC: SimaSpec = {
  glass: { x: SIMATUOPPI.x, y: SIMATUOPPI.y + TUOPPI.height / 2, z: SIMATUOPPI.z },
  taps: BAARI.tapX.map((x) => ({
    x,
    // The nozzle hangs off the end of the arm out over the counter: see
    // `buildBaari`, which draws it at `tower - 0.18` and 0.1 m deep.
    y: BAARI.top + BAARI.towerHeight - 0.13,
    z: BAARI.tapZ - 0.22,
  })),
  gate: 'porukka-tiella',
  additionalGates: [POYTAPORUKKA.name],
  crowd: [...PORUKKA_CROWD, ...POYTAPORUKKA_CROWD],
};


/**
 * A gravel strip laid between two points rather than along an axis.
 *
 * Floors are the one thing in the blockout that may be rotated - nothing
 * collides with them - and the yard needs it: a drive that runs in 90-degree
 * steps reads as a corridor, and the whole point of the piha is that it is one
 * open surface you pick your own line across.
 */
export function driveway(
  name: string,
  from: { x: number; z: number },
  to: { x: number; z: number },
  width: number,
  material: MaterialKey = 'gravel',
): FloorSpec {
  const length = Math.hypot(to.x - from.x, to.z - from.z);
  const center = { x: (from.x + to.x) / 2, z: (from.z + to.z) / 2 };
  return {
    name,
    material,
    center: [center.x, center.z],
    size: [width, length],
    y: material === 'dirt' ? 0.02 : 0.01,
    // The quad's length axis is its local +y, which lands on -z once the plane
    // is laid flat - the same convention `yawTowards` is written in.
    rotationY: yawTowards(from, to),
  };
}

/**
 * A window on the navetta's south face: white frame, red surround, the way
 * every window in the pihapiiri is painted.
 */
function barnWindow(index: number, cx: number): DecorSpec[] {
  const face = -29.65;
  const sill = 1.05;
  return [
    decor(`navetan-ikkuna-${index}-kehys`, 'faluRed', cx - 0.72, face - 0.06, cx + 0.72, face + 0.02, 1.26, sill - 0.08),
    decor(`navetan-ikkuna-${index}`, 'porcelain', cx - 0.62, face - 0.04, cx + 0.62, face + 0.06, 1.1, sill),
    decor(`navetan-ikkuna-${index}-lasi`, 'carGlass', cx - 0.54, face + 0.02, cx + 0.54, face + 0.08, 0.94, sill + 0.08),
  ];
}

export const NAVETTA_WINDOWS = [133.6, 137.4, 143.2, 150.6].flatMap((cx, index) =>
  barnWindow(index + 1, cx),
);

/* --------------------------------------------------------- tornimajoitus - */

/**
 * The old feed silo, slept in now, standing at the juhlanavetta's east gable -
 * which is where the venue's own is. A silo is built against the end of the
 * building it fed, not left out in a field, and putting it back there is what
 * the yard reads by: come up the pihatie and the barn's facade runs away to the
 * right until the drum stops it, three metres taller than the ridge.
 *
 * It stands 0.7 m clear of the barn's roof overhang: close enough to belong to
 * the building, and not so close that the two silhouettes merge into one lump
 * on the way past.
 *
 * Same split as the kuusikko and the paalit: the drum is decor and the box
 * inscribed in it is the collider, because a cylinder is not something
 * collision here can be asked about cheaply. Nothing runs out here - the route
 * turns into the porch thirty metres short of it - so the box is about the
 * shape of the pihapiiri rather than about any line.
 */
export const TORNI = {
  x: 160.0,
  z: -33.0,
  radius: 3.4,
  height: 5.4,
  /** The cone on top of it, and its own overhang past the drum. */
  roofHeight: 1.8,
  roofRadius: 3.8,
  /** Half the square inscribed in the drum: `radius / Math.SQRT2`, rounded in. */
  inscribed: 2.4,
} as const;

/* ------------------------------------------------- tornimajoituksen ovi -- */

/**
 * A door on a round wall.
 *
 * Boards here are axis-aligned, and the drum is a polygon: the flat leaf is
 * tangent at `TORNI.z + TORNI.radius` and the wall behind it recedes towards
 * both jambs. So every board is pushed out past the tangent point far enough
 * to stay proud of the wall at its own edges, and buried deep enough at the
 * back that no two faces ever end up on the same plane.
 */
function tornimajoituksenOvi(): { decor: DecorSpec[]; handles: HandleSpec[] } {
  const face = TORNI.z + TORNI.radius;
  const half = 0.45;
  const height = 2.05;
  // Hinged on the east jamb, so the leaf opens away from the polku people come
  // up in the dark.
  const leaf = decor('tornin-ovi', 'door', TORNI.x - half, face - 0.15, TORNI.x + half, face + 0.05, height);
  return {
    decor: [
      leaf,
      // Karmi: two jambs and a head, standing a little further out again.
      decor('tornin-karmi-lansi', 'furniture', TORNI.x - half - 0.08, face - 0.18, TORNI.x - half, face + 0.1, height + 0.08),
      decor('tornin-karmi-ita', 'furniture', TORNI.x + half, face - 0.18, TORNI.x + half + 0.08, face + 0.1, height + 0.08),
      decor('tornin-karmi-yla', 'furniture', TORNI.x - half - 0.08, face - 0.18, TORNI.x + half + 0.08, face + 0.1, 0.08, height),
    ],
    // The painike on the +z face, and only that one - see `keittion-painike`,
    // which is the same handle on a flat wall. Behind this leaf is the box
    // inscribed in the drum, and there is nobody in there to reach for it.
    handles: [handle('tornin-painike', leaf.box, 1, 1)],
  };
}

export const TORNIN_OVI = tornimajoituksenOvi();
